import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "../helpers/click_outside_mixin"

// Locally filtered combobox for a small in-memory catalog (instrument codes).
export default class extends Controller {
  static targets = ["input", "hidden", "listbox", "menu"]
  static values = {
    options: { type: Array, default: [] },
    emptyLabel: { type: String, default: "" }
  }

  connect() {
    this._activeIndex = -1
    this._visible = this.optionsValue
    this._clickOutside = useClickOutside(this.element, () => this.closeMenu())
  }

  disconnect() {
    this._clickOutside?.remove()
  }

  onFocus() {
    this.filter(this.inputTarget.value)
    this.openMenu()
  }

  search() {
    this.filter(this.inputTarget.value)
    this.openMenu()
  }

  filter(query) {
    const normalized = query.trim().toLowerCase()
    this._visible = this.optionsValue.filter((option) => {
      if (!normalized) return true
      return String(option.label || "").toLowerCase().includes(normalized) ||
        String(option.code || "").toLowerCase().includes(normalized)
    })
    this._activeIndex = -1
    this.renderOptions()
  }

  renderOptions() {
    this.listboxTarget.replaceChildren()
    if (this._visible.length === 0) {
      const empty = document.createElement("p")
      empty.className = "psychologist-client-search__empty"
      empty.textContent = this.emptyLabelValue || this.inputTarget.getAttribute("placeholder") || ""
      this.listboxTarget.appendChild(empty)
      return
    }

    this._visible.forEach((option, index) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "psychologist-client-search__option"
      button.role = "option"
      button.id = `${this.listboxTarget.id}-option-${index}`
      button.dataset.code = option.code
      button.dataset.label = option.label
      button.dataset.action = "click->catalog-combobox#selectOption"
      button.setAttribute("aria-selected", "false")

      const label = document.createElement("span")
      label.className = "psychologist-client-search__option-label"
      label.textContent = option.label
      button.appendChild(label)

      if (option.featured) {
        const meta = document.createElement("span")
        meta.className = "psychologist-client-search__option-meta"
        meta.textContent = this.element.dataset.featuredLabel || ""
        if (meta.textContent) button.appendChild(meta)
      }

      this.listboxTarget.appendChild(button)
    })
  }

  selectOption(event) {
    this.applySelection(event.currentTarget.dataset.code, event.currentTarget.dataset.label)
  }

  applySelection(code, label) {
    this.hiddenTarget.value = code
    this.inputTarget.value = label
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.closeMenu()
  }

  onKeydown(event) {
    if (!this.menuTarget.hidden && this._visible.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        this.moveActive(1)
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        this.moveActive(-1)
        return
      }
      if (event.key === "Enter") {
        const option = this.optionButtons()[this._activeIndex]
        if (option) {
          event.preventDefault()
          this.applySelection(option.dataset.code, option.dataset.label)
        }
        return
      }
    }

    if (event.key === "Escape") {
      this.closeMenu()
    }
  }

  moveActive(delta) {
    const buttons = this.optionButtons()
    if (buttons.length === 0) return
    this._activeIndex = Math.max(0, Math.min(buttons.length - 1, this._activeIndex + delta))
    buttons.forEach((button, index) => {
      const active = index === this._activeIndex
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-selected", active ? "true" : "false")
      if (active) button.scrollIntoView({ block: "nearest" })
    })
  }

  optionButtons() {
    return Array.from(this.listboxTarget.querySelectorAll(".psychologist-client-search__option"))
  }

  openMenu() {
    this.menuTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this._clickOutside.add()
  }

  closeMenu() {
    this.menuTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this._clickOutside.remove()
    this._activeIndex = -1
  }
}
